import type { Interpolation, Theme } from '@emotion/react';
import type { IconName } from '@frontend/icons';
import { ResourcePillType } from './components/avatar-pill/resource-pill';

export type CalendarColumnBodyVariant = 'grid' | 'stacked';
export type CalendarTimeIndicatorLines = 'hourly' | 'half-hourly' | 'none';
export type CalendarColumnHeaderType = 'weekday' | 'provider' | 'operatory' | 'default';

export type CalendarGridDragCreateSelection = {
  columnId: string;
  groupId: string;
  startMinutes: number;
  endMinutes: number;
};

export type CalendarGridCreateEvent = CalendarGridDragCreateSelection;
export const CalendarEventTypeArr = [
  'appointment',
  'scheduleRequest',
  'cancelledAppointment',
  'scheduleDefinition',
  'exception', // stripe lines with label
  'unavailableBlock', // stripe lines
  'availableBlock', // white block
  'availabilitySlot', // blue block with time label
  'workingHoursV3',
  'nonWorkingHoursV3',
  // availability finder specific event types
  'nonInteractiveSlot',
  'slotsStartLabel',
] as const;

export type CalendarEventType = (typeof CalendarEventTypeArr)[number];

export const isCalendarEventType = (value: string): value is CalendarEventType => {
  return CalendarEventTypeArr.includes(value as CalendarEventType);
};

type BaseCalendarEvent<TEventType extends CalendarEventType> = {
  id: string;
  startTime: string; // "HH:mm" format, e.g. "14:30"
  duration?: number; // in minutes
  eventType: TEventType;
  /** true when the event actually started on the previous day and is clipped to the visible day's start */
  continuesFromPreviousDay?: boolean;
  /** true when the event actually ends on the next day and is clipped to the visible day's end */
  continuesToNextDay?: boolean;
};

export type AppointmentCalendarEvent = BaseCalendarEvent<'appointment'>;
export type ScheduleRequestCalendarEvent = BaseCalendarEvent<'scheduleRequest'>;
export type CancelledAppointmentCalendarEvent = BaseCalendarEvent<'cancelledAppointment'>;
export type ScheduleDefinitionCalendarEvent = BaseCalendarEvent<'scheduleDefinition'>;
export type ExceptionCalendarEvent = BaseCalendarEvent<'exception'>;
export type UnavailableBlockCalendarEvent = BaseCalendarEvent<'unavailableBlock'>;
export type AvailableBlockCalendarEvent = BaseCalendarEvent<'availableBlock'>;
export type AvailabilitySlotCalendarEvent = BaseCalendarEvent<'availabilitySlot'>;
export type NonInteractiveSlotCalendarEvent = BaseCalendarEvent<'nonInteractiveSlot'>;
export type SlotsStartLabelCalendarEvent = BaseCalendarEvent<'slotsStartLabel'> & {
  label?: string;
};
export type WorkingHoursV3CalendarEvent = BaseCalendarEvent<'workingHoursV3'>;
export type NonWorkingHoursV3CalendarEvent = BaseCalendarEvent<'nonWorkingHoursV3'>;

export type CalendarEvent =
  | AppointmentCalendarEvent
  | ScheduleRequestCalendarEvent
  | CancelledAppointmentCalendarEvent
  | ScheduleDefinitionCalendarEvent
  | ExceptionCalendarEvent
  | UnavailableBlockCalendarEvent
  | AvailableBlockCalendarEvent
  | AvailabilitySlotCalendarEvent
  | NonInteractiveSlotCalendarEvent
  | SlotsStartLabelCalendarEvent
  | WorkingHoursV3CalendarEvent
  | NonWorkingHoursV3CalendarEvent;

export type CalendarColumnGroup = {
  id: string;
  name: string;
  columnIds: string[];
  groupOffset: number; // for calculating the position of the group in the grid
  timezone?: string; // optional timezone for the group, e.g. "America/New_York"
  emptyStateVariant?: 'no-events' | 'no-columns'; // optional empty state variant for the group
};

type BaseCalendarColumn = {
  id: string;
  name: string;
  eventIds: string[];
  groupId: string;
};

export type WeekdayCalendarColumn = BaseCalendarColumn & {
  slotsCount: number;
  dayOfMonth: number; // for date-based columns, e.g. "1" for 1st of the month
};

export type ProviderCalendarColumn = BaseCalendarColumn & {
  providerImage: string;
  type?: ResourcePillType;
  count?: number;
};

export type OperatoryCalendarColumn = BaseCalendarColumn & {
  operatoryIcon: IconName;
  operatoryIconColor?: string;
  count?: number;
};

type CalendarColumnByHeaderType = {
  weekday: WeekdayCalendarColumn;
  provider: ProviderCalendarColumn;
  operatory: OperatoryCalendarColumn;
  default: BaseCalendarColumn;
};

export type CalendarColumn<THeader extends CalendarColumnHeaderType = CalendarColumnHeaderType> =
  CalendarColumnByHeaderType[THeader];

export interface CalendarNavigationConfig {
  onNext: () => void;
  onPrevious: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

export type AllDayCalendarEventScope = 'group' | 'column';

export type AllDayCalendarEvent = {
  id: string;
  name: string;
  /** 'group' spans all columns in the group; 'column' is confined to a single column */
  scope: AllDayCalendarEventScope;
  groupId: string;
  /** Required when scope === 'column' */
  columnId?: string;
  icon?: IconName;
};

/** Contextual information passed to the renderEvent render prop. */
export type CalendarEventRenderContext = {
  columnId: string;
  groupId: string;
};

export type EventDropColumnStyleFn = (
  draggedEvent: CalendarEvent,
  columnId: string,
  groupId: string
) => Interpolation<Theme>;
export type EventOverlayStyleFn = (draggedEvent: CalendarEvent) => Interpolation<Theme>;
export type ColumnHeaderOverlayStyleFn = (columnId: string, groupId: string) => Interpolation<Theme>;

export type CalendarGridDragStyles = {
  /** Applied to the source event card while it is being dragged. Default: opacity 0. */
  eventCardOverlay?: Interpolation<Theme> | EventOverlayStyleFn;
  /** Applied to the DragOverlay ghost card. Default: opacity 0.85. */
  eventDragOverlay?: Interpolation<Theme> | EventOverlayStyleFn;
  /** Applied to a column body while an event is dragged over it. */
  eventDropColumn?: Interpolation<Theme> | EventDropColumnStyleFn;
  /**
   * Applied to an auto-positioned placeholder div inside the hovered column.
   * The widget sets position, pointerEvents, top, and height automatically from previewMinutes + duration.
   * Supply border, background, borderRadius, left, right, etc.
   * Pass a function to vary styles per dragged event type.
   */
  eventDropOverlay?: Interpolation<Theme> | EventOverlayStyleFn;
  /** Applied to a column header while it is being reorder-dragged. Default: opacity 0. */
  columnHeaderOverlay?: Interpolation<Theme> | ColumnHeaderOverlayStyleFn;
  /** Applied to the DragOverlay ghost of a column header during reorder. */
  columnHeaderDragOverlay?: Interpolation<Theme> | ColumnHeaderOverlayStyleFn;
};

export type CalendarGridRenderProp = (event: CalendarEvent) => React.ReactNode;

export type EmptyStateRenderProp = (groupIds: string[], variant: 'no-events' | 'no-columns') => React.ReactNode | null;
